import _ from 'lodash';
import Lenis from 'lenis';
import gsap from 'gsap';
import { Common } from './common';
import 'lenis/dist/lenis.css';
import { scrollToHash, isMobileOrTablet } from './utils';
import { SelectCourse } from './component/selectcourse';

// Lenis のインスタンスをグローバルに持っておく（後で destroy できるように）
let lenis: Lenis | null = null;
let navlenis: Lenis | null = null;
let modallenis: Lenis | null = null;
const isMobile = isMobileOrTablet();

const initLenis = () => {
  if (isMobile) {
    document.body.classList.add('device-mobile');
  }
  // ---------- メインの Lenis ----------
  if (!isMobile) {
    lenis = new Lenis({
      lerp: 0.1,
      // smoothWheel: true, // 必要なら
    });

    const raf = (time: number) => {
      lenis!.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    lenis.on('scroll', () => {
      Common.scroll();
    });
  }
  // モバイルなら lenis は null のまま → ネイティブスクロールが有効

  // ---------- Nav 内 Lenis ----------
  const Nav = document.getElementById('Nav') as HTMLElement;
  if (!Nav) return;

  const NavWrapper = Nav.querySelector('.l-nav__wrapper') as HTMLElement;
  const NavContainer = Nav.querySelector('.l-nav__container') as HTMLElement;

  if (!isMobile && NavWrapper && NavContainer) {
    navlenis = new Lenis({
      lerp: 0.1,
      wrapper: NavWrapper,
      content: NavContainer,
    });

    const navraf = (time: number) => {
      navlenis!.raf(time);
      requestAnimationFrame(navraf);
    };
    requestAnimationFrame(navraf);

    // 初期状態は止めておく（ナビが閉じているとき用）
    navlenis.stop();
  }
};

const destroyLenis = () => {
  if (lenis) {
    lenis.destroy();
    lenis = null;
  }
  if (navlenis) {
    navlenis.destroy();
    navlenis = null;
  }
};

const init = () => {
  initLenis(); // ← ここでデバイス判定して Lenis を作る／作らないを決定

  // Nav 開閉（Lenis が存在しない＝モバイルの場合は単にクラス切替だけ）
  const navOpenBtn = document.getElementById('navOpenBtn') as HTMLButtonElement;
  const navCloseBtn = document.getElementById('navCloseBtn') as HTMLButtonElement;
  const Nav = document.getElementById('Nav') as HTMLElement;

  navOpenBtn?.addEventListener('click', () => {
    document.body.classList.add('is-nav-open');

    if (navlenis) {
      navlenis.start();
    }
    if (lenis) {
      lenis.stop();
    }

    gsap.to(Nav, { autoAlpha: 1 });
  });

  navCloseBtn?.addEventListener('click', () => {
    document.body.classList.remove('is-nav-open');

    if (navlenis) {
      navlenis.stop();
    }
    if (lenis) {
      lenis.start();
    }

    gsap.to(Nav, { autoAlpha: 0 });
  });

  // navlink のクリック（ハンバーガーメニュー内のアンカー）
  const navlinks = document.querySelectorAll('[data-navlink-id]') as NodeListOf<HTMLElement>;
  navlinks.forEach((navlink) => {
    navlink.addEventListener('click', () => {
      const id = navlink.dataset.navlinkId as string;

      document.body.classList.remove('is-nav-open');

      if (navlenis) navlenis.stop();
      if (lenis) lenis.start();

      gsap.to(Nav, {
        autoAlpha: 0,
        onComplete: () => {
          scrollToHash(`#${id}`, null);
        },
      });
    });
  });

  // modals
  const modalTriggers = document.querySelectorAll<HTMLElement>('[data-mopen]');
  const modalIds = new Set;
  if (modalTriggers.length) {
    modalTriggers.forEach(trigger => {
      const id = trigger.getAttribute('data-mopen') as string;
      const modal = document.getElementById(id);
      if (modal) {
        modalIds.add(id);
        trigger.addEventListener('click', () => {
          openModal(id);
        });
      }
    });
  }
  if (modalIds.size > 0) {
    modalIds.forEach(id => {
      const closes = document.querySelectorAll(`#${id} [data-mclose]`);
      if (closes.length) {
        closes.forEach(close => {
          close.addEventListener('click', () => {
            closeModal(id);
          });
        });
      }
    });
  }
};

const openModal = (id: string) => {
  const wrapper = document.getElementById(id);
  if (!wrapper) return;
  const content = wrapper.querySelector('.c-modal__wrapper');
  modallenis?.destroy();
  modallenis = null;
  lenis?.stop();

  if (!content) return;

  gsap.to(wrapper, {
    autoAlpha: 1,
    duration: 0.3
  });

  if (isMobile) {
    document.body.style.overflow = 'hidden';
  } else {
    modallenis = new Lenis({
      wrapper,
      content,
      lerp: 0.1,
    });

    const modalraf = (time: number) => {
      modallenis!.raf(time);
      requestAnimationFrame(modalraf);
    };
    requestAnimationFrame(modalraf);
  }
}

const closeModal = (id: string) => {
  const modal = document.getElementById(id);

  gsap.to(modal, {
    autoAlpha: 0,
    duration: 0.3
  });

  if (isMobile) {
    document.body.style.overflow = '';
  } else {
    modallenis?.destroy();
    modallenis = null;

    lenis?.start();
  }
}

(function () {
  window.addEventListener('DOMContentLoaded', () => {
    document.body.style.overflow = 'hidden';
    Common.init();
    SelectCourse.init();
  });

  const handleResize = _.debounce(() => {
    Common.resize();

    // リサイズ時にブレークポイントが変わった可能性があるので再初期化
    // （PC ↔ スマホ切り替えに対応したい場合）
    destroyLenis();
    initLenis(); // Lenis を作り直す
  });
  window.addEventListener('resize', handleResize);

  const handleScroll = _.debounce(() => {
    Common.scroll();
  });
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('breakPoint', (e) => {
    Common.breakPoint();
    SelectCourse.breakPoint(e.detail.is_mobile);
  });

  window.addEventListener('load', () => {
    const url = new URL(location.href);
    const mvVideo: HTMLVideoElement | null = document.querySelector('.l-mv__video');
    const loading = document.querySelector('.l-loading') as HTMLElement;
    const loadingLogo = loading.querySelector('.c-main-copy__logo') as HTMLElement;
    const loadingTexts = loading.querySelectorAll('.c-main-copy__txt span') as NodeListOf<HTMLElement>;
    const isEnglish = document.querySelector('html')?.getAttribute('lang') === 'en';

    if (url.searchParams.get('develop') === 'true') {
      document.body.style.overflow = '';
      loading.remove();
      init();
    } else {
      const tl = gsap.timeline();
      tl.to(loadingLogo, { duration: 1, opacity: 1, filter: 'blur(0px)' })
        .to(loadingLogo, { duration: 0.5, y: 0 })
        .to(
          loadingTexts,
          {
            scale: 1,
            duration: 1,
            opacity: 1,
            filter: 'blur(0px)',
            stagger: function (index) {
              return isEnglish ? index * 0.02 : index * 0.07;
            },
          },
          '-=0.5'
        )
        .to(
          loading,
          {
            autoAlpha: 0,
            duration: 2,
            onStart: () => {
              if (mvVideo) {
                mvVideo.load();
                setTimeout(() => mvVideo.play(), 100);
              }
            },
            onComplete: () => {
              document.body.style.overflow = '';
              loading.remove();
              init();
            },
          },
          '+=0.5'
        );
    }
  });
})();
